// Utility functions for consistent assessment scoring and color coding
// Color system based on exact score percentage: 100% = GREEN, 0% = RED

export interface ScoreColors {
  high: string;
  moderate: string;
  low: string;
}

export const DEFAULT_SCORE_COLORS: ScoreColors = {
  high: '#059669',    // GREEN for high scores (70%+)
  moderate: '#ea580c', // ORANGE for middle scores (40-69%)
  low: '#dc2626',     // RED for low scores (<40%)
};

export const DEFAULT_SCORE_DOTS = {
  high: 1,    // 1 dot for high scores
  moderate: 2, // 2 dots for middle scores
  low: 3,     // 3 dots for low scores
};

/**
 * Get color and dots based on numerical score percentage
 * @param score - The actual numerical score
 * @param maxScore - The maximum possible score
 * @param colors - Optional custom colors (defaults to DEFAULT_SCORE_COLORS)
 * @returns Object with color and dots count
 */
export const getNumericalScoreDisplay = (
  score: number,
  maxScore: number,
  colors: ScoreColors = DEFAULT_SCORE_COLORS
) => {
  const percentage = (score / maxScore) * 100;

  if (percentage >= 70) {
    return {
      color: colors.high,
      dots: DEFAULT_SCORE_DOTS.high,
      level: 'High'
    };
  } else if (percentage >= 40) {
    return {
      color: colors.moderate,
      dots: DEFAULT_SCORE_DOTS.moderate,
      level: 'Moderate'
    };
  } else {
    return {
      color: colors.low,
      dots: DEFAULT_SCORE_DOTS.low,
      level: 'Low'
    };
  }
};

/**
 * Get proportional color based on exact score percentage using bucketed colors
 * @param score - The actual numerical score
 * @param maxScore - The maximum possible score
 * @returns Color string (Red for Low, Orange for Moderate, Green for High)
 */
export const getProportionalScoreColor = (score: number, maxScore: number): string => {
  const percentage = Math.max(0, Math.min(100, (score / maxScore) * 100));

  // Use bucketed colors for clear visual distinction:
  // 70%+ = Green (High)
  // 40-69% = Orange (Moderate)
  // 0-39% = Red (Low)
  if (percentage >= 70) {
    return DEFAULT_SCORE_COLORS.high; // Green
  } else if (percentage >= 40) {
    return DEFAULT_SCORE_COLORS.moderate; // Orange
  } else {
    return DEFAULT_SCORE_COLORS.low; // Red
  }
};

/**
 * Get color based on numerical score percentage (legacy bucketed system)
 * @param score - The actual numerical score
 * @param maxScore - The maximum possible score
 * @param colors - Optional custom colors
 * @returns Color string
 */
export const getNumericalScoreColor = (
  score: number,
  maxScore: number,
  colors: ScoreColors = DEFAULT_SCORE_COLORS
): string => {
  return getNumericalScoreDisplay(score, maxScore, colors).color;
};

/**
 * Get dots count based on numerical score percentage
 * @param score - The actual numerical score
 * @param maxScore - The maximum possible score
 * @returns Number of dots (1-3)
 */
export const getNumericalScoreDots = (
  score: number,
  maxScore: number
): number => {
  return getNumericalScoreDisplay(score, maxScore).dots;
};
/**
 * Get level string based on numerical score percentage
 * @param score - The actual numerical score
 * @param maxScore - The maximum possible score
 * @returns Level string ('High', 'Moderate', or 'Low')
 */
export const getNumericalScoreLevel = (
  score: number,
  maxScore: number
): string => {
  return getNumericalScoreDisplay(score, maxScore).level;
};

/**
 * Clamp a percentage value to the valid range of 0-100
 * @param percentage - The raw percentage value
 * @returns Clamped percentage between 0 and 100
 */
export const clampPercentage = (percentage: number): number => {
  return Math.max(0, Math.min(100, percentage));
};


